import { useCallback, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { useLazyGetPickerOptionsQuery } from 'api/pickerData'
import {
  loadBottomData,
  loadTopData,
  selectBottomPage,
  selectBottomSearchTerm,
  selectBottomSelectedItems,
  selectLevel1EndpointConfig,
  selectLevel1KeyField,
  selectLevel1ValueField,
  selectLevel2EndpointConfig,
  selectLevel2KeyField,
  selectLevel2ValueField,
  selectTopPage,
  selectTopSearchTerm,
  selectTopSelectedItem,
  setBottomTotalCount,
} from 'slices/aiSegmentBuilder/picker'
import type { EndpointConfig } from 'slices/aiSegmentBuilder/picker'

// ═══════════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════════

interface UsePickerDataFetchingOptions {
  pickerId: number
  level: 'top' | 'bottom'
  /** Debounce delay in ms (default: 300) */
  debounceMs?: number
  /** Page size for pagination (default: 50) */
  pageSize?: number
}

interface UsePickerDataFetchingResult {
  /** Trigger a search with the given term */
  search: (term: string) => void
  /** Trigger a page change */
  goToPage: (page: number) => void
  /** Whether a fetch is currently in progress */
  isLoading: boolean
  /** Refetch with current params */
  refetch: () => void
}

// ═══════════════════════════════════════════════════════════════════════════
// Helper: Interpolate params
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Interpolates special placeholders in endpoint params.
 * Currently supports :level_1 for bottom dropdown filter.
 */
function interpolateParams(
  params: Record<string, unknown>,
  context: { topSelectedItem?: string | null }
): Record<string, unknown> {
  const result: Record<string, unknown> = {}

  for (const [key, value] of Object.entries(params)) {
    if (value === ':level_1' && context.topSelectedItem) {
      result[key] = context.topSelectedItem
    } else if (value === ':level_1' && !context.topSelectedItem) {
      // Skip this param if no top item selected
      continue
    } else {
      result[key] = value
    }
  }

  return result
}

/**
 * Interpolates :placeholder segments in the endpoint URL path using resolved params.
 * Params consumed by URL interpolation are removed from the returned params object
 * so they aren't also sent as query parameters.
 *
 * Example:
 *   endpoint: "user_tag_keys/:key/values", params: { key: "favorite_location" }
 *   => { endpoint: "user_tag_keys/favorite_location/values", params: {} }
 */
export function interpolateEndpoint(
  endpoint: string,
  params: Record<string, unknown>
): { endpoint: string; params: Record<string, unknown> } {
  let interpolatedEndpoint = endpoint
  const remainingParams = { ...params }

  for (const [key, value] of Object.entries(params)) {
    const placeholder = `:${key}`
    if (interpolatedEndpoint.includes(placeholder) && value != null) {
      interpolatedEndpoint = interpolatedEndpoint.replace(
        placeholder,
        encodeURIComponent(String(value))
      )
      delete remainingParams[key]
    }
  }

  return { endpoint: interpolatedEndpoint, params: remainingParams }
}

// ═══════════════════════════════════════════════════════════════════════════
// Hook
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Hook for fetching picker data using endpoint config from Redux.
 * Handles debouncing, pagination, and param interpolation.
 */
export function usePickerDataFetching({
  pickerId,
  level,
  debounceMs = 300,
  pageSize = 50,
}: UsePickerDataFetchingOptions): UsePickerDataFetchingResult {
  const dispatch = useDispatch()
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)
  const lastFetchParamsRef = useRef<string>('')
  const hasInitialFetchedRef = useRef(false)

  // RTK Query lazy query
  // Use isFetching (not isLoading) to show spinner on ALL fetches, not just initial
  const [fetchPickerOptions, { isFetching }] = useLazyGetPickerOptionsQuery()

  // Select endpoint config from group config based on level (uses pickerToGroup mapping)
  const endpointConfig = useSelector(
    useCallback(
      (state: any) =>
        level === 'top'
          ? selectLevel1EndpointConfig(state, pickerId)
          : selectLevel2EndpointConfig(state, pickerId),
      [level, pickerId]
    )
  )

  // Select keyField and valueField from group config
  const keyField = useSelector(
    useCallback(
      (state: any) =>
        level === 'top'
          ? selectLevel1KeyField(state, pickerId)
          : selectLevel2KeyField(state, pickerId),
      [level, pickerId]
    )
  )

  const valueField = useSelector(
    useCallback(
      (state: any) =>
        level === 'top'
          ? selectLevel1ValueField(state, pickerId)
          : selectLevel2ValueField(state, pickerId),
      [level, pickerId]
    )
  )

  // Select current search term and page
  const searchTerm = useSelector(
    useCallback(
      (state: any) =>
        level === 'top'
          ? selectTopSearchTerm(state, pickerId)
          : selectBottomSearchTerm(state, pickerId),
      [pickerId, level]
    )
  )

  const page = useSelector(
    useCallback(
      (state: any) =>
        level === 'top'
          ? selectTopPage(state, pickerId)
          : selectBottomPage(state, pickerId),
      [pickerId, level]
    )
  )

  // For bottom level, we need the top selected item for interpolation
  const topSelectedItem = useSelector(
    useCallback(
      (state: any) =>
        level === 'bottom' ? selectTopSelectedItem(state, pickerId) : null,
      [pickerId, level]
    )
  )

  // Check if picker already has selections (indicates it was hydrated from server)
  // Used to skip initial fetch for hydrated pickers
  const hasExistingTopSelection = useSelector(
    useCallback(
      (state: any) =>
        level === 'top'
          ? selectTopSelectedItem(state, pickerId) !== null
          : false,
      [pickerId, level]
    )
  )
  const hasExistingBottomSelections = useSelector(
    useCallback(
      (state: any) =>
        level === 'bottom'
          ? selectBottomSelectedItems(state, pickerId).length > 0
          : false,
      [pickerId, level]
    )
  )
  const hasExistingSelections =
    hasExistingTopSelection || hasExistingBottomSelections

  // Core fetch function
  const doFetch = useCallback(
    async (
      config: EndpointConfig,
      query: string,
      pageNum: number,
      topItem: string | null,
      options: { preferCacheValue?: boolean } = {}
    ) => {
      // Interpolate params (e.g., replace :level_1 with actual value)
      const interpolatedParams = interpolateParams(config.params, {
        topSelectedItem: topItem,
      })

      // Interpolate URL path placeholders (e.g., :key) using resolved params
      const { endpoint: resolvedEndpoint, params: remainingParams } =
        interpolateEndpoint(config.endpoint, interpolatedParams)

      // Build a cache key to avoid duplicate fetches within this hook
      const fetchKey = JSON.stringify({
        endpoint: resolvedEndpoint,
        params: remainingParams,
        query,
        page: pageNum,
      })

      // Skip if same as last fetch (unless we're explicitly preferring cache)
      // When preferCacheValue is true, RTK Query will return cached data if available
      if (
        fetchKey === lastFetchParamsRef.current &&
        !options.preferCacheValue
      ) {
        return
      }
      lastFetchParamsRef.current = fetchKey

      try {
        const result = await fetchPickerOptions(
          {
            endpoint: resolvedEndpoint,
            page: pageNum,
            perPage: pageSize,
            query: query || undefined,
            additionalParams: remainingParams as Record<
              string,
              string | number | string[] | boolean
            >,
            keyField,
            valueField,
          },
          // preferCacheValue: true returns cached data without network request if available
          options.preferCacheValue ?? false
        )

        if (result.data?.items) {
          const dataItems = result.data.items.map(item => ({
            id: String(item.id),
            name: item.name,
          }))

          // Fall back to items count when API doesn't return pagination
          const totalCount =
            result.data.pagination?.total_count ?? dataItems.length
          const effectivePageSize = result.data.pagination?.per_page || pageSize

          if (level === 'top') {
            dispatch(
              loadTopData({
                pickerId,
                data: dataItems,
                totalCount,
                pageSize: effectivePageSize,
              })
            )
          } else {
            dispatch(
              loadBottomData({
                pickerId,
                data: dataItems,
                totalCount,
                pageSize: effectivePageSize,
              })
            )
            dispatch(
              setBottomTotalCount({
                pickerId,
                totalCount,
              })
            )
          }
        }
      } catch (error) {
        // Failed to fetch - keep existing data
        // console.error('Failed to fetch picker data:', error)
      }
    },
    [
      dispatch,
      fetchPickerOptions,
      level,
      pickerId,
      pageSize,
      keyField,
      valueField,
    ]
  )

  // Debounced search handler
  const search = useCallback(
    (term: string) => {
      // Clear any pending debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }

      // Debounce the fetch
      debounceTimerRef.current = setTimeout(() => {
        if (endpointConfig) {
          // Reset to page 1 on new search
          doFetch(endpointConfig, term, 1, topSelectedItem)
        }
      }, debounceMs)
    },
    [endpointConfig, doFetch, debounceMs, topSelectedItem]
  )

  // Page change handler (no debounce needed)
  const goToPage = useCallback(
    (pageNum: number) => {
      if (endpointConfig) {
        doFetch(endpointConfig, searchTerm, pageNum, topSelectedItem)
      }
    },
    [endpointConfig, doFetch, searchTerm, topSelectedItem]
  )

  // Refetch with current params - uses RTK Query cache if available
  const refetch = useCallback(() => {
    if (endpointConfig) {
      // Use preferCacheValue to return cached data instantly if available
      // This avoids redundant network requests when reopening dropdown
      doFetch(endpointConfig, searchTerm, page, topSelectedItem, {
        preferCacheValue: true,
      })
    }
  }, [endpointConfig, doFetch, searchTerm, page, topSelectedItem])

  // Initial fetch when endpoint config becomes available
  // Skip if picker already has selections (hydrated from server data)
  // Uses cache to avoid redundant requests if data already fetched
  useEffect(() => {
    // Skip initial fetch if picker was hydrated with selections
    if (hasExistingSelections) {
      hasInitialFetchedRef.current = true
      return
    }
    // Only fetch once on initial mount
    if (endpointConfig && !hasInitialFetchedRef.current) {
      hasInitialFetchedRef.current = true
      doFetch(endpointConfig, searchTerm, page, topSelectedItem, {
        preferCacheValue: true,
      })
    }
  }, [endpointConfig, hasExistingSelections]) // eslint-disable-line react-hooks/exhaustive-deps

  // Track previous top selection to detect changes
  const prevTopSelectedItemRef = useRef<string | null | undefined>(undefined)

  // Refetch when top selection changes (for bottom dropdown)
  useEffect(() => {
    if (level === 'bottom' && endpointConfig) {
      // Skip initial mount (when prevRef is undefined)
      if (prevTopSelectedItemRef.current === undefined) {
        prevTopSelectedItemRef.current = topSelectedItem
        return
      }

      // Only refetch if the value actually changed
      if (prevTopSelectedItemRef.current !== topSelectedItem) {
        prevTopSelectedItemRef.current = topSelectedItem
        // Clear cache to force refetch with new filter
        lastFetchParamsRef.current = ''
        doFetch(endpointConfig, searchTerm, 1, topSelectedItem)
      }
    }
  }, [topSelectedItem]) // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [])

  return {
    search,
    goToPage,
    isLoading: isFetching,
    refetch,
  }
}

export default usePickerDataFetching
